import React, { useEffect, useMemo, useState } from 'react';
import {
	Button,
	CardActions,
	CardContent,
	Divider,
	TextField,
	FormHelperText,
	CircularProgress,
	FormLabel,
	Container,
	Stack,
	FormControl,
	Grid,
	Typography,
	useTheme,
} from '@mui/material';
import { Formik, Form, useFormikContext } from 'formik';
import { useClientStorageHooks } from '@/hooks/useClientStorageHooks';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { useDispatch, useSelector } from 'react-redux';
import { validationSchema } from './validationSchema';
import {
	useBatchGetDealListPoliciesQuery,
	useCreateRenewalMutation,
	useGetDealRowQuery,
} from '@/features/deals/dealsApi';
import { InstantClientSelect } from '@/components/selects/ClientSelect';
import FileUpload from '@/components/FileUpload';
import { PageHeader } from '@/components/layouts/PageHeader';
import { showToast } from '@/features/toast/toastSlice';
import { useLocation, useNavigate } from 'react-router-dom';
import { FormikErrorScroll, useFormikHelper } from '@/hooks/useFormikHelper';
import { reset } from '@/features/select/clientSelectSlice';
import {
	ADDITIONAL_GOALS_OF_COVER,
	ADDITIONAL_SPECIALIST_PRODUCTS,
	ADVISER_FEE,
	BROKER_GROUP_ID,
	CLIENT_CATEGORY,
	CLIENT_ID,
	CLIENT_TYPE,
	COMMERCIAL,
	DEAL_NAME,
	DEAL_OVERVIEW,
	GOALS_OF_COVER,
	NEEDS_ANALYSIS,
	OWNER_DOC_ID,
	RENEWAL_DATE,
	SCOPE_OF_ADVICE,
	YEAR_FOLDER,
} from '../CreateNewBusinessForm/validationSchema';
import { paths } from '@/app/routes';
import { getPipelineIcon } from '@/utils/constants';
import { useGetClientPropertiesQuery } from '@/features/clients/clientsApi';
import { AdditionalScopeFields } from '../CreateNewBusinessForm/Fields/AdditionalScopeFields';
import { useGetDealNoteSummariesQuery } from '@/features/factFind/factFindApi';
import {
	NOTES_HELP_TEXT,
	SEARCH_CLIENT_HELP_TEXT,
	UPLOAD_FILES_HELP_TEXT,
} from '@/utils/helperText';
import {
	default as folderValidationSchema,
	FOLDER_NAME,
} from '@/components/dialogs/AddFolderSharepointDialog/validationSchema';
import DealNaming from '../fields/DealNaming';
import DevJSONView from '@/components/DevJSONView';
import FormNavigationBlocker from '../FormNavigationBlocker';
import { useGetInsightPolicyQuery } from '@/features/policies/policiesApi';
import { PolicySelectionSync } from './PolicySelectionSync';
import { safelyParseDate } from '@/utils/date';
import { uniqueId } from 'lodash';
import { usePoliciesSelect } from '@/context/policiesContext';
import DealFormMessages from '@/components/forms/CreateFormFields/DealFormMessages';
import DealFormActions from '@/components/forms/CreateFormFields/DealFormActions';
import RenewalDateField from '@/components/forms/CreateFormFields/RenewalDateField';
import AdviserFeeField from '@/components/forms/CreateFormFields/AdviserFeeField';
import InsurancePortfolioSelect from '@/components/forms/CreateRenewalForm/InsurancePortfolioSelect';

export const CreateRenewalForm = () => {
	const dispatch = useDispatch();
	const location = useLocation();
	const navigate = useNavigate();
	const { setSelectedPolicies, clearSelectedPolicies } = usePoliciesSelect();

	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;
	const { data: userDetails } = useGetUserDetailsQuery(
		{ email },
		{
			skip: !email,
		},
	);
	const existingDealIds = useMemo(
		() => location?.state?.dealIds ?? [],
		[location?.state?.dealIds],
	);

	const { data: policy } = useGetInsightPolicyQuery(location?.state?.policyId, {
		skip: !location?.state?.policyId,
	});

	const existingPolicy = useMemo(
		() => location?.state?.policy ?? policy ?? {},
		[location?.state?.policy, policy],
	);

	const hasExistingPolicy = useMemo(
		() => Object.keys(existingPolicy ?? {}).length > 0 && existingPolicy?.Id != null,
		[existingPolicy],
	);

	const policiesQuery = useBatchGetDealListPoliciesQuery(
		{ dealIds: existingDealIds },
		{
			skip:
				existingDealIds.length === 0 ||
				(location?.state?.policies ?? []).length > 0 ||
				hasExistingPolicy,
		},
	);

	const policiesByDealIds = useMemo(() => {
		const policyData = policiesQuery.data ?? {};
		return existingDealIds.flatMap((dealId) => {
			const policies = (policyData.policiesByDealId?.[dealId] ?? []).map((p) => p.policy);
			const policyStatus = policyData.statusByDealId?.[dealId];

			const policiesLoading =
				policiesQuery.isUninitialized || !policyStatus || policyStatus === 'loading';

			return [...policies.map((p) => ({ ...p, policyStatus, policiesLoading }))];
		});
	}, [policiesQuery.data, existingDealIds]);

	const existingPolicies = useMemo(() => {
		const fromState = location?.state?.policies ?? [];
		const array =
			fromState.length > 0
				? fromState
				: hasExistingPolicy
					? [existingPolicy]
					: (policiesByDealIds ?? []);

		return array
			?.map((policyItem) => {
				const nestedPolicy = policyItem?.policy ?? policyItem;
				const id = policyItem.insightId ?? policyItem.Id ?? nestedPolicy?.Id;
				if (id == null) {
					return null;
				}

				return {
					Id: id,
					policyId: id,
					insightId: id,
					policy: nestedPolicy,
					policyTransId:
						policyItem.policyTransId ??
						policyItem.latestTransactionId ??
						nestedPolicy?.CurrentPolicy ??
						nestedPolicy?.latestTransactionId,
					renewalDate:
						policyItem?.renewalDate ?? nestedPolicy?.ToDate ?? nestedPolicy?.toDate,
				};
			})
			.filter(Boolean);
	}, [location?.state?.policies, policiesByDealIds, existingPolicy, hasExistingPolicy]);

	const [formLoading, setFormLoading] = useState(false);
	const [brokerGroupId, setBrokerGroupId] = useState(userDetails?.brokerGroupId);

	useEffect(() => {
		if (!brokerGroupId && userDetails?.brokerGroupId)
			setBrokerGroupId(userDetails.brokerGroupId);
	}, [brokerGroupId, userDetails?.brokerGroupId]);

	const initialValues = {
		policy: null,
		policyId: -1,
		// Context is source of truth; PolicySelectionSync copies selectedPolicies here
		policies: [],
		files: [],
		clientName: '',
		[BROKER_GROUP_ID]: userDetails?.brokerGroupId,
		[OWNER_DOC_ID]: userDetails?.userId,
		successMessage: '',
		errorMessage: '',
		[YEAR_FOLDER]: null,
		[DEAL_NAME]: '',
		[FOLDER_NAME]: '',
		[DEAL_OVERVIEW]: '',
		dealIds: [],
		[RENEWAL_DATE]: null,
		[CLIENT_ID]: '',
		[ADVISER_FEE]: '',
		[GOALS_OF_COVER]: [],
		[CLIENT_CATEGORY]: [],
		[SCOPE_OF_ADVICE]: [],
		[NEEDS_ANALYSIS]: [],
		[CLIENT_TYPE]: COMMERCIAL,
		[ADDITIONAL_SPECIALIST_PRODUCTS]: '',
		portfolioTemplate: null,
	};

	const [schema, setSchema] = useState(validationSchema);

	useEffect(() => {
		if (existingPolicies.length === 0) {
			return;
		}
		setSelectedPolicies(existingPolicies);
	}, [existingPolicies, setSelectedPolicies]);

	const { setUpDealFilesAndFolders } = useClientStorageHooks(brokerGroupId);

	const [createRenewal] = useCreateRenewalMutation();

	const createRenewalFolders = async (values, dealId, noteId) => {
		try {
			await setUpDealFilesAndFolders({
				dealId,
				noteId,
				clientId: values.clientId,
				clientName: values.clientName,
				dealName: values[FOLDER_NAME],
				pipelineId: process.env.REACT_APP_PIPELINE_RENEWALS,
				files: values.files,
				yearFolder: values.yearFolder,
				notes: values.notes,
			});
		} catch (error) {
			console.log('🚀 ~ Error creating renewal folders', error);
			return {
				error: {
					message: `Renewal ${dealId} created, but there was an error creating renewal folder or uploading files`,
				},
			};
		}
	};

	const createNewRenewal = async (values) => {
		const body = {
			ownerDocId: values[OWNER_DOC_ID] ?? userDetails?.userId,
			dealName: values[FOLDER_NAME].trim(),
			dealOverview: (values[DEAL_OVERVIEW] ?? '').trim(),
			notes: values.notes,
			clientId: values.clientId,
			brokerGroupId: values[BROKER_GROUP_ID],
			policyIds: values.policies.map((p) => p.Id ?? p.policyId ?? p?.insightId),
			[YEAR_FOLDER]: values[YEAR_FOLDER],
			...(values.dealIds.length > 0 && {
				previousDealIds: values.dealIds,
			}),
			adviserFee: values[ADVISER_FEE],
			clientGoals: values[GOALS_OF_COVER],
			additionalGoals: values[ADDITIONAL_GOALS_OF_COVER],
			clientCategory: values.clientCategory,
			clientType: values.clientType,
			needsAnalysis: values[NEEDS_ANALYSIS],
			additionalProducts: values.additionalSpecialistProducts,
			riskClasses: values[SCOPE_OF_ADVICE],
		};
		if (values.renewalDate) {
			const renewalDate = safelyParseDate(values.renewalDate);
			const timezoneOffset = renewalDate.getTimezoneOffset();
			renewalDate.setMinutes(renewalDate.getMinutes() - timezoneOffset);
			const timestamp = renewalDate.setUTCHours(0, 0, 0, 0);
			body.renewalDate = timestamp;
		}
		if (values.portfolioTemplate) {
			body.portfolioTemplateId = values.portfolioTemplate?.Id;
		}
		return await createRenewal(body)
			.unwrap()
			.catch((err) => {
				console.log('🚀 ~ Error creating renewal', err);
				return { error: { message: 'Error creating renewal' } };
			});
	};

	return (
		<Formik
			initialValues={initialValues}
			validateOnChange={false}
			validationSchema={schema}
			enableReinitialize
			onSubmit={async (values, { resetForm, setFieldValue, setSubmitting }) => {
				setFormLoading(true);
				const toastId = uniqueId();
				console.log('📋 ~ Formik values', values);
				const createError = (message) => {
					dispatch(
						showToast({
							toastId,
							message: `Error creating renewal ${values[FOLDER_NAME]}`,
							autohide: false,
						}),
					);
					setFieldValue('errorMessage', message ?? 'Error creating renewal');
					setFieldValue('successMessage', '');
					setSubmitting(false);
					setFormLoading(false);
				};

				dispatch(
					showToast({
						toastId,
						message: 'Creating renewal...',
						action: {
							path: `${paths.renewals}`,
							label: 'GO BACK TO ALL RENEWALS',
						},
						autohide: false,
					}),
				);

				const res = await createNewRenewal(values);
				if (res?.error) {
					console.log('🙅 ~ Error creating renewal', res?.error);
					createError(res?.error?.message);
					return;
				}
				const response = await createRenewalFolders(values, res.dealId, res.noteId);

				if (response?.error) {
					createError(response?.error?.message);
					dispatch(
						showToast({
							toastId,
							message:
								response?.error?.message ??
								`${values[FOLDER_NAME]} Renewal created!`,
							action: {
								path: `${paths.renewals}/${res.dealId}`,
								label: 'GO TO RENEWAL',
							},
							autohide: false,
						}),
					);
					return;
				}
				console.log('🚀 ~ Successfully created new Renewal', res);

				dispatch(
					showToast({
						toastId,
						message: `${values[FOLDER_NAME]} Renewal created!`,
						action: {
							path: `${paths.renewals}/${res.dealId}`,
							label: 'GO TO RENEWAL',
						},
						autohide: false,
					}),
				);

				const isOnCreateRenewal = window.location.pathname.includes(paths.createRenewal);

				if (isOnCreateRenewal) {
					window.scrollTo(0, 0);
					navigate(location.pathname, { replace: true, state: {} });
					clearSelectedPolicies();
					resetForm();
					dispatch(reset());
					setFieldValue('successMessage', 'Successfully created Renewal');
					setFieldValue('errorMessage', '');
					setSubmitting(false);
					setFormLoading(false);
				}
			}}
		>
			<CreateRenewalFormBody
				{...{
					formLoading,
					setFormLoading,
					setSchema,
					setBrokerGroupId,
				}}
			/>
		</Formik>
	);
};

const CreateRenewalFormBody = ({ formLoading, setFormLoading, setSchema, setBrokerGroupId }) => {
	const theme = useTheme();
	const location = useLocation();
	const { setSelectedPolicies } = usePoliciesSelect();

	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;
	const {
		data: userDetails,
		isLoading,
		isUninitialized,
	} = useGetUserDetailsQuery(
		{ email },
		{
			skip: !email,
		},
	);
	const existingClientId = useMemo(() => location?.state?.clientId, [location.state]);

	const { data: clientData } = useGetClientPropertiesQuery(existingClientId, {
		skip: !existingClientId,
	});
	const clientRow = clientData?.clientRow;

	const existingClient = useMemo(() => {
		return clientRow ?? location?.state?.client ?? {};
	}, [location?.state?.client, clientRow]);

	const isExistingClient = useMemo(
		() => Object.keys(existingClient)?.length > 0 || existingClientId,
		[existingClient],
	);

	const existingDealIds = useMemo(
		() => location?.state?.dealIds ?? [],
		[location?.state?.dealIds],
	);

	const { data: policy } = useGetInsightPolicyQuery(location?.state?.policyId, {
		skip: !location?.state?.policyId,
	});

	const existingPolicy = useMemo(
		() => location?.state?.policy ?? policy ?? {},
		[location?.state?.policy, policy],
	);

	const hasExistingPolicy = useMemo(
		() => Object.keys(existingPolicy ?? {}).length > 0 && existingPolicy?.Id != null,
		[existingPolicy],
	);

	const policiesQuery = useBatchGetDealListPoliciesQuery(
		{ dealIds: existingDealIds },
		{
			skip:
				existingDealIds.length === 0 ||
				(location?.state?.policies ?? []).length > 0 ||
				hasExistingPolicy,
		},
	);

	const policiesByDealIds = useMemo(() => {
		const policyData = policiesQuery.data ?? {};
		return existingDealIds.flatMap((dealId) => {
			const policies = (policyData.policiesByDealId?.[dealId] ?? []).map((p) => p.policy);
			const policyStatus = policyData.statusByDealId?.[dealId];

			const policiesLoading =
				policiesQuery.isUninitialized || !policyStatus || policyStatus === 'loading';

			return [...policies.map((p) => ({ ...p, policyStatus, policiesLoading }))];
		});
	}, [policiesQuery.data, existingDealIds]);

	const existingPolicies = useMemo(() => {
		const fromState = location?.state?.policies ?? [];
		const array =
			fromState.length > 0
				? fromState
				: hasExistingPolicy
					? [existingPolicy]
					: (policiesByDealIds ?? []);

		return array
			?.map((policyItem) => {
				const nestedPolicy = policyItem?.policy ?? policyItem;
				const id = policyItem.insightId ?? policyItem.Id ?? nestedPolicy?.Id;
				if (id == null) {
					return null;
				}

				return {
					Id: id,
					policyId: id,
					insightId: id,
					policy: nestedPolicy,
					policyTransId:
						policyItem.policyTransId ??
						policyItem.latestTransactionId ??
						nestedPolicy?.CurrentPolicy ??
						nestedPolicy?.latestTransactionId,
					renewalDate:
						policyItem?.renewalDate ?? nestedPolicy?.ToDate ?? nestedPolicy?.toDate,
				};
			})
			.filter(Boolean);
	}, [location?.state?.policies, policiesByDealIds, existingPolicy, hasExistingPolicy]);

	const { values, isSubmitting, errors, setFieldValue, setValues, getFieldProps } =
		useFormikContext();
	const { getError, getErrorMessage } = useFormikHelper();

	const loading = isSubmitting || formLoading;

	useEffect(() => {
		setSchema(
			validationSchema
				.omit([FOLDER_NAME])
				.concat(folderValidationSchema(values.existingFolders, 'Deal name', true)),
		);
	}, [values.existingFolders]);

	const dealRowQuery = useGetDealRowQuery(values.dealIds[0], {
		skip: values.dealIds.length === 0,
	});
	const dealRow = useMemo(
		() => (values.dealIds.length === 0 ? {} : dealRowQuery.data),
		[dealRowQuery.data, values.dealIds],
	);

	const noteSummaryQuery = useGetDealNoteSummariesQuery(
		{ dealId: values.dealIds[0] },
		{
			skip: values.dealIds.length === 0,
		},
	);

	const summaries = useMemo(
		() => (values.dealIds.length === 0 ? {} : (noteSummaryQuery.data?.summary ?? [])),
		[noteSummaryQuery.data?.summary, values.dealIds],
	);

	useEffect(() => {
		setFieldValue('dealIds', existingDealIds ?? []);
	}, [existingDealIds]);

	useEffect(() => {
		setFieldValue('notes', summaries[0]?.content?.[0]?.text?.value ?? '');
	}, [summaries]);

	useEffect(() => {
		setFieldValue(ADVISER_FEE, dealRow?.[ADVISER_FEE]);
		setFieldValue(GOALS_OF_COVER, dealRow?.[GOALS_OF_COVER] ?? []);
		setFieldValue(CLIENT_CATEGORY, dealRow?.[CLIENT_CATEGORY] ?? []);
		setFieldValue(SCOPE_OF_ADVICE, dealRow?.[SCOPE_OF_ADVICE] ?? []);
		setFieldValue(NEEDS_ANALYSIS, dealRow?.[NEEDS_ANALYSIS] ?? []);
		setFieldValue(
			CLIENT_TYPE,
			(dealRow?.[CLIENT_TYPE] ?? '').length > 0 ? dealRow?.[CLIENT_TYPE] : COMMERCIAL,
		);
		setFieldValue(
			ADDITIONAL_SPECIALIST_PRODUCTS,
			dealRow?.[ADDITIONAL_SPECIALIST_PRODUCTS] ?? '',
		);
	}, [dealRow]);

	useEffect(() => {
		const dates = existingPolicies
			.map((p) => {
				const dateValue =
					p.renewalDate ?? p.toDate ?? p.ToDate ?? p.policy?.ToDate ?? p.policy?.toDate;
				return dateValue ? safelyParseDate(dateValue) : null;
			})
			.filter(Boolean)
			.sort((a, b) => a - b);
		const renewalDate = dates[0];

		setFieldValue('policy', existingPolicy);
		setFieldValue('policyId', existingPolicy?.Id ?? -1);

		setFieldValue(
			RENEWAL_DATE,
			renewalDate ??
				(existingPolicy?.ToDate ? safelyParseDate(existingPolicy?.ToDate) : null),
		);
	}, [existingPolicy, existingPolicies]);

	useEffect(() => {
		setFieldValue('clientName', existingClient?.name ?? '');
		if (existingClient?.brokerGroupId)
			setFieldValue(BROKER_GROUP_ID, existingClient.brokerGroupId);
		setFieldValue(CLIENT_ID, existingClient?.clientId ?? existingClient?.id ?? '');
	}, [existingClient]);

	useEffect(() => {
		if (clientRow?.brokerGroupId) setBrokerGroupId(clientRow?.brokerGroupId);
	}, [clientRow?.brokerGroupId]);

	const gridProps = {
		rowSpacing: 2,
		columnSpacing: 2,
	};
	return (
		<Container sx={{ pt: 3 }}>
			<Form>
				<FormNavigationBlocker />
				<FormikErrorScroll isSubmitting={isSubmitting} />
				<PageHeader
					title={'Add Renewal'}
					icon={getPipelineIcon(
						process.env.REACT_APP_PIPELINE_RENEWALS,
						'40px',
						theme.palette.mode === 'dark' ? { bgColor: 'transparent' } : undefined,
					)}
				/>
				<CardContent>
					{isLoading || isUninitialized ? (
						<CircularProgress />
					) : (
						<Stack spacing={gridProps.rowSpacing}>
							<Grid container {...gridProps}>
								<DealNaming />
							</Grid>
							<Typography variant='form_label'>Client details</Typography>

							<FormControl fullWidth>
								<FormLabel required>
									{isExistingClient ? 'Client name' : 'Search client name'}
								</FormLabel>
								<InstantClientSelect
									clientId={values.clientId}
									clientName={values.clientName}
									filterProspects={true}
									setClient={(newClient) => {
										console.log('Here is the selected client: ', newClient);
										if (newClient) {
											setValues({
												...values,
												clientId: newClient.id,
												clientName: newClient.name,
												[BROKER_GROUP_ID]:
													newClient.brokerGroupId ??
													userDetails?.brokerGroupId,
											});
										} else {
											setSelectedPolicies(Array.from(existingPolicies ?? []));
											setValues({
												...values,
												clientId: null,
												dealIds: [],
												dealName: '',
												clientName: '',
												policy: existingPolicy ?? null,
												policyId: existingPolicy?.Id ?? -1,
												[BROKER_GROUP_ID]: userDetails?.brokerGroupId,
											});
										}
									}}
									disabled={loading || isExistingClient}
								/>
								<FormHelperText>{SEARCH_CLIENT_HELP_TEXT}</FormHelperText>
							</FormControl>

							<Typography variant='form_label'>Deal details</Typography>
							<Grid container {...gridProps}>
								<Grid size={12}>
									<FormLabel
										sx={{ display: 'flex' }}
										error={Boolean(errors.policyId)}
									>
										Policies for renewal
									</FormLabel>

									<Stack>
										<FormHelperText
											sx={{ ml: 0 }}
											error={Boolean(errors.policies)}
										>
											Select one or more policies for renewal
										</FormHelperText>
										<PolicySelectionSync statusId={0} typeId={0} />
										{errors.policies && (
											<FormHelperText error>{errors.policies}</FormHelperText>
										)}
									</Stack>
								</Grid>

								<Grid size={12}>
									<AdditionalScopeFields
										loading={loading}
										disabled={isSubmitting}
									/>
								</Grid>

								<Grid size={12}>
									<FormLabel>{'Notes'}</FormLabel>
									<TextField
										fullWidth
										id='notes'
										multiline
										rows={4}
										{...getFieldProps('notes')}
										disabled={loading}
										error={getError('notes')}
										helperText={getErrorMessage('notes')}
									/>
									<FormHelperText>{NOTES_HELP_TEXT}</FormHelperText>
								</Grid>
								<Grid size={12}>
									<FileUpload
										files={values.files}
										setFiles={(files) => setFieldValue('files', files)}
										loading={loading}
									/>
									<FormHelperText>{UPLOAD_FILES_HELP_TEXT}</FormHelperText>
								</Grid>
								<Grid size={{ xs: 12, md: 4 }}>
									<RenewalDateField />
								</Grid>
								<Grid size={{ xs: 12, md: 4 }}>
									<AdviserFeeField loading={loading} />
								</Grid>
								<Grid size={{ xs: 12, md: 4 }}>
									<InsurancePortfolioSelect />
								</Grid>
							</Grid>

							<DealFormMessages />
						</Stack>
					)}
				</CardContent>
				<DevJSONView value={values} />
				<Divider />
				<DealFormActions disabled={isLoading || isUninitialized || formLoading} />
			</Form>
		</Container>
	);
};
